import { ComputersHealthFilterType } from 'mbs-ui-shared@models/Computer';
import { RmmPageFilteringOP, RmmPageFilterItem, RmmPageFilterItemField } from 'mbs-ui-shared@models/rmm-warehouse.models';

const isHiddenField = 'computer.isHidden';
const computerStateField = 'summary.computerState';

// The warehouse counts "problem" computers by summary.computerState "Alarm", not "Problem"
const computerStatePerHealthFilter: Partial<Record<ComputersHealthFilterType, string>> = {
  [ComputersHealthFilterType.RMMProblem]: 'Alarm',
  [ComputersHealthFilterType.RMMWarning]: 'Warning'
};

function flatConditions(conditions: RmmPageFilterItemField[]): RmmPageFilterItem[] {
  return conditions.flatMap((group) => (group.conditions ?? []) as RmmPageFilterItem[]);
}

function withoutField(conditions: RmmPageFilterItemField[], field: string): RmmPageFilterItemField[] {
  return conditions.filter((group) => !flatConditions([group]).some((condition) => condition.field === field));
}

export function rmmPageFieldMatchesAny(field: string, op: RmmPageFilteringOP, values: unknown[]): RmmPageFilterItemField {
  return { logic: 'or', conditions: values.map((value) => ({ field, op, value })) };
}

export function rmmPageFieldMatchesAll(field: string, op: RmmPageFilteringOP, values: unknown[]): RmmPageFilterItemField {
  return { logic: 'and', conditions: values.map((value) => ({ field, op, value })) };
}

export function toRmmPageQueryFilter(conditions: RmmPageFilterItemField[]): RmmPageFilterItemField | null {
  return conditions.length ? { logic: 'and', conditions } : null;
}

// TBD: only RMM problem / warning narrow the list. Pending needs `computer.isPending`; Unsupported / Outdated /
// Install issues need an agent version status field
export function rmmPageStatisticConditions(statisticFilter: ComputersHealthFilterType): RmmPageFilterItemField[] {
  const computerState = computerStatePerHealthFilter[statisticFilter];

  return computerState ? [rmmPageFieldMatchesAny(computerStateField, RmmPageFilteringOP.Eq, [computerState])] : [];
}

export function splitRmmPageFilter(conditions: RmmPageFilterItemField[]): {
  searchConditions: RmmPageFilterItemField[];
  statisticFilter: ComputersHealthFilterType;
} {
  const computerState = flatConditions(conditions).find((condition) => condition.field === computerStateField)?.value;
  const statisticFilter = Object.entries(computerStatePerHealthFilter).find(([, state]) => state === computerState)?.[0];

  return {
    searchConditions: withoutField(conditions, computerStateField),
    statisticFilter: (statisticFilter as ComputersHealthFilterType) ?? ComputersHealthFilterType.All
  };
}

// TBD: Hidden is kept with the search conditions for now; whether it should be its own filter is not decided yet
export function hasRmmPageHiddenFilter(conditions: RmmPageFilterItemField[]): boolean {
  return flatConditions(conditions).some((condition) => condition.field === isHiddenField && condition.value === true);
}

export function withRmmPageHiddenFilter(conditions: RmmPageFilterItemField[], hidden: boolean): RmmPageFilterItemField[] {
  const otherConditions = withoutField(conditions, isHiddenField);

  return hidden ? [...otherConditions, rmmPageFieldMatchesAny(isHiddenField, RmmPageFilteringOP.Eq, [true])] : otherConditions;
}
